import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import BadgesComponent from "@components/common/Badges";
import Button from "@components/common/Button";
import { coverageShiftTransfer, facilityDetailsForHealthProviderTransfer } from '@constants/index';
import CommonFormFields from '@pages/Forms/CommonFormFields';
import endpoints from '@config/endpoints';
import { getData, getDataParams, getDataWithCache, postDataJSON } from '@services/CommonApiService';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { errorLogger } from '@utils/applicationLogUtils';
import isUsingGramPanchayatUtils from '@utils/isUsingGrampanchayatUtil';
import _ from 'lodash';
import './ShiftTransferComponent.css';
import {
  SHIFT_TRANSFER_CONSTANTS,
  SHIFT_TRANSFER_FORM_DEFAULTS,
  RURAL_URBAN_OPTIONS,
  prePopulateCoverageDetails,
  preShiftTransferDetails,
  hpShiftFinalData,
  hpApprovalPayload
} from './shiftTransferConstants';
import axios from '@utils/axiosExt';
import Auth from '@utils/auth';
import ApprovalModal from '@pages/Approval/HealthFacility/approvalModal';
import { approvalStatusUpdate } from '@services/approvalApiServices';
import { useSelector } from 'react-redux';
import { RootState } from '@store/reducers';
import { STATUS, approvalConstantData, checkUserRole, errorSuccessMessage, roleType } from '@constants/approvalConstants';
import { decodeWithKeyAndShift } from '@utils/base64Utils';

interface objectTypes {
  isError: boolean,
  message: string,
  isDisable: boolean,
  value: any,
  id?: any,
  verifiedDetails?: any
  code?: number
}

interface formStateType {
  [key: string]: objectTypes
}

interface formDataType {
  [key: string]: any;
}

interface ShiftTransferComponentProps {
  healthProviderData: any;
  onCancel: () => void;
}

const ShiftTransferComponent: React.FC<ShiftTransferComponentProps> = ({
  healthProviderData,
  onCancel
}) => {
  const currentData = useMemo(() => healthProviderData?.current_data, [healthProviderData]);
  const userDetails = useMemo(() => currentData?.user_details?.[0], [currentData]);
  const locationDetails = useMemo(() => currentData?.location_details, [currentData]);
  const effectiveFromDate = useMemo(() => healthProviderData?.effective_from_date, [healthProviderData]);

  const formattedUserDetails = useMemo(() => ({
    ...userDetails,
    state_id: locationDetails?.state?.state_id,
    state_code: locationDetails?.state?.state_code,
    state_name: locationDetails?.state?.state_name,
    district_id: locationDetails?.district?.district_id,
    district_code: locationDetails?.district?.district_code,
    district_name: locationDetails?.district?.district_name,
    usertype_name: userDetails?.user_type
  }), [userDetails, locationDetails]);

  const [facilityDetails1, setFacilityDetails1] = useState<any[]>();

  const mobiliserTaggedDetails = useMemo(() => formattedUserDetails?.user_type === SHIFT_TRANSFER_CONSTANTS.USER_TYPES.FACILITY_LEVEL
    ? (locationDetails?.mobiliser_details || []).map((item: any) => ({
      id: item.mobiliser_id,
      value: `${item.mobiliser_name}`,
      label: `${item.mobiliser_name}`,
      code: item.mobiliser_code
    }))
    : (locationDetails?.health_provider_details || []).map((item: any) => ({
      id: item.health_provider_id,
      value: `${item.health_provider_name}`,
      label: `${item.health_provider_name}`,
      code: item.health_provider_code
    })), [formattedUserDetails?.user_type, locationDetails?.mobiliser_details, locationDetails?.health_provider_details]);

  const coverageShiftTransferObj = SHIFT_TRANSFER_FORM_DEFAULTS.coverageShiftTransferObj;
  const shiftTransferObj = SHIFT_TRANSFER_FORM_DEFAULTS.shiftTransferObj;

  const [isGrampanchayat, setIsGrampanchayat] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  const hasInitialized = useRef<boolean>(false);
  const [shiftTransferForm, setShiftTransferForm] = useState<formDataType>(shiftTransferObj);
  const [coverageShiftTransferData, setCoverageShiftTransferData] = useState<formDataType>(coverageShiftTransfer);
  const [coverageShiftTransferForm, setCoverageShiftTransferForm] = useState<formStateType>(coverageShiftTransferObj);
  const [shiftTransferData, setShiftTransferData] = useState<formDataType>(facilityDetailsForHealthProviderTransfer);
  const approvalLayout = useSelector((state: RootState) => state.approvalWorkFlow);

  const [ulbInputText, setUlbInputText] = useState<string>('');
  const [shiftTransferBlocks, setShiftTransferBlocks] = useState<any>();
  const [shiftTransferULBs, setShiftTransferULBs] = useState<any>();
  const [shiftTransferDistricts, setShiftTransferDistricts] = useState<any>();
  const [showApprovalModal, setShowApprovalModal] = useState<boolean>(false);
  const healthProvider = useRef<any>(null);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const encodedUserRoles = localStorage.getItem('userRoleDetails');
  const userRolesDetails = encodedUserRoles ? JSON.parse(decodeWithKeyAndShift(encodedUserRoles)).split(', ') : [];
  const roleData = checkUserRole(userRolesDetails)
  const navigate = useNavigate();

  const prepopulateFormData = useCallback(async () => {
    const locationData = locationDetails;
    if (!locationData) return;

    setShiftTransferForm((prevState: any) => ({
      ...prevState,
      ...preShiftTransferDetails(locationData, effectiveFromDate)
    }));

    try {
      if (locationData.state?.state_id) {

        setShiftTransferData((prevState: any) => ({
          ...prevState,
          state: {
            ...prevState.state,
            '#isDisabled': true,
          },
          rural_urban: {
            ...prevState.rural_urban,
            '#options': RURAL_URBAN_OPTIONS,
          },
        }));
      }

      if (locationData.district?.district_id) {
        const [subDistrictResponse, blockResponse, healthBlockResponse, subDivisionResponse] = await Promise.all([
          getData(`${endpoints.getSubDistricts(locationData.district.district_id)}`),
          getData(`${endpoints.getBlock(locationData.district.district_id)}`),
          getData(`${endpoints.getHealthBlock(locationData.district.district_id)}`),
          getData(`${endpoints.getSubDivision(locationData.district.district_id)}`)
        ]);

        const subdistrict_options = (subDistrictResponse as any)?.data?.map((key: any) => ({
          value: key.sub_district_name + "-" + key.sub_district_code,
          label: key.sub_district_name + "-" + key.sub_district_code,
          id: key.sub_district_id,
          code: key.sub_district_code,
        }));

        const block_options = (blockResponse as any)?.data?.map((key: any) => ({
          value: key.block_name + "-" + key.block_code,
          label: key.block_name + "-" + key.block_code,
          id: key.block_id,
          code: key.block_code,
        }));

        let healthblock_options = (healthBlockResponse as any)?.data?.map((key: any) => ({
          value: key.heal_block_name + "-" + key.heal_block_code,
          label: key.heal_block_name + "-" + key.heal_block_code,
          id: key.health_block_id,
          code: key.heal_block_code,
        }));
        healthblock_options = healthblock_options.filter((item: any) => item.value);

        const subdivision_options = (subDivisionResponse as any)?.data?.map((key: any) => ({
          value: key.sub_division_name + "-" + key.sub_division_code,
          label: key.sub_division_name + "-" + key.sub_division_code,
          id: key.sub_division_id,
          code: key.sub_division_code,
        }));

        setShiftTransferData((prevState: any) => ({
          ...prevState,
          sub_district: {
            ...prevState.sub_district,
            '#options': subdistrict_options,
          },
          block: {
            ...prevState.block,
            '#options': block_options,
          },
          health_block: {
            ...prevState.health_block,
            '#options': healthblock_options,
          },
          sub_division: {
            ...prevState.sub_division,
            '#options': subdivision_options,
          },
        }));
      }

      if (locationData.block?.block_id) {

        if (isGrampanchayat) {
          const gramPanchayat = await getData<any>(`${endpoints.getGramPanchayatByBlockId(locationData.block.block_id)}`);
          const gramPanchayatOptions = gramPanchayat?.data.map((option1: any) => ({
            value: `${option1.gram_panchayat_name}-${option1.gram_panchayat_code}`,
            label: `${option1.gram_panchayat_name}-${option1.gram_panchayat_code}`,
            id: option1.gram_panchayat_id,
            code: option1.gram_panchayat_code
          }));
          setShiftTransferData((prevState: any) => ({
            ...prevState,
            gram_panchayat: {
              ...prevState?.gram_panchayat,
              '#options': gramPanchayatOptions,
            },
          }));
        } else {
          const villagesResponse = await axios.get(`${endpoints.getVillageByBlockId(locationData.block.block_id)}`, Auth.loggedIn());
          const village_options = villagesResponse?.data?.data.map((key: any) => ({
            value: key.village_name + "-" + key.village_code,
            label: key.village_name + "-" + key.village_code,
            id: key.village_id
          }))
          setShiftTransferData((create_facilitydetails: { village: any; }) => ({
            ...create_facilitydetails,
            village: {
              ...create_facilitydetails.village,
              '#options': village_options
            }
          }))
        }

        const { facility_hfr_options, facility_name_options, facility_type_options } = await fetchAndReturnHFRIdTypeAndName(isGrampanchayat ? locationData.gram_panchayat?.gram_panchayat_id : locationData.village?.village_id, isGrampanchayat ? 'gram_panchayat' : 'village', locationData.district.district_id);
        await setFacilityCoverageData(facility_name_options, facility_hfr_options, facility_type_options, locationData)
      }

      if (locationData.ulb?.ulb_id) {
        const wardResponse = await getData(`${endpoints.getWard(locationData.ulb.ulb_id)}`) as any;
        let ward_options = wardResponse?.data?.map((key: any) => ({
          value: key.ward_name + "-" + key.ward_code,
          label: key.ward_name + "-" + key.ward_code,
          id: key.ward_id,
          code: key.ward_code,
        }));
        ward_options = ward_options.filter((item: any) => item.value && !item.value.includes('undefined'));

        setShiftTransferData((prevState: any) => ({
          ...prevState,
          ward: {
            ...prevState?.ward,
            '#options': ward_options,
          },
        }));

        const { facility_hfr_options, facility_name_options, facility_type_options } = await fetchAndReturnHFRIdTypeAndName(locationData.ward?.ward_id, 'ward', locationData.district.district_id);
        await setFacilityCoverageData(facility_name_options, facility_hfr_options, facility_type_options, locationData)

      }

    } catch (error) {
      errorLogger(error);
    }

    if (locationData.facility_details) {
      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        ...prePopulateCoverageDetails(locationData)
      }));

      if (locationData.facility_details.facility_id) {
        try {
          await fetchVillageWardAndAshaShiftTransfer(locationData.facility_details.facility_id);
        } catch (error) {
          errorLogger(error);
        }
      }
    }

    if (locationData.urban_rual?.urban_rural_name) {
      const ruralUrbanValue = locationData.urban_rual.urban_rural_name === 'rural' ? 'Rural' : 'Urban';

      if (ruralUrbanValue === "Rural") {
        setShiftTransferData((role_details: any) => ({
          ...role_details,
          ulb: {
            ...role_details?.ulb,
            '#isDisabled': true,
            '#required': false,
          },
          ward: {
            ...role_details?.ward,
            '#isDisabled': true,
            '#required': false,
          },
          village: {
            ...role_details?.village,
            '#isDisabled': false,
            '#required': false,
          },
          gram_panchayat: {
            ...role_details?.gram_panchayat,
            '#isDisabled': false,
            '#required': false,
          },
          block: {
            ...role_details?.block,
            '#isDisabled': false,
            '#required': false,
          },
        }));

        setCoverageShiftTransferData((coverage_details: any) => ({
          ...coverage_details,
          wards_covered: {
            ...coverage_details?.wards_covered,
            '#isDisabled': true,
          },
          villages_covered: {
            ...coverage_details?.villages_covered,
            '#isDisabled': false,
          },
          gram_panchayat_covered: {
            ...coverage_details?.gram_panchayat_covered,
            '#isDisabled': false,
          },
        }));
      } else if (ruralUrbanValue === "Urban") {
        setShiftTransferData((shift_transfer: any) => ({
          ...shift_transfer,
          ulb: {
            ...shift_transfer?.ulb,
            '#isDisabled': false,
            '#required': true,
          },
          ward: {
            ...shift_transfer?.ward,
            '#isDisabled': false,
            '#required': false,
          },
          village: {
            ...shift_transfer?.village,
            '#isDisabled': true,
            '#required': false,
          },
          gram_panchayat: {
            ...shift_transfer?.gram_panchayat,
            '#isDisabled': true,
            '#required': false,
          },
          block: {
            ...shift_transfer?.block,
            '#isDisabled': true,
            '#required': false,
          },
        }));

        setCoverageShiftTransferData((coverage_details: any) => ({
          ...coverage_details,
          wards_covered: {
            ...coverage_details?.wards_covered,
            '#isDisabled': false,
          },
          villages_covered: {
            ...coverage_details?.villages_covered,
            '#isDisabled': true,
          },
          gram_panchayat_covered: {
            ...coverage_details?.gram_panchayat_covered,
            '#isDisabled': true,
          },
        }));
      }
    }

    if (approvalLayout.requestData.requestFor == approvalConstantData.requestFor.received && roleData == roleType.only_approver) {
      setShiftTransferData((role_details: any) => ({
        ...role_details,
        efective_from_date: {
          ...role_details?.efective_from_date,
          '#isDisabled': true,
        },
        state: {
          ...role_details?.state,
          '#isDisabled': true,
        },
        district: {
          ...role_details?.district,
          '#isDisabled': true,
        },
        rural_urban: {
          ...role_details?.rural_urban,
          '#isDisabled': true,
        },
        ulb: {
          ...role_details?.ulb,
          '#isDisabled': true,
        },
        sub_district: {
          ...role_details?.sub_district,
          '#isDisabled': true,
        },
        ward: {
          ...role_details?.ward,
          '#isDisabled': true,
        },
        village: {
          ...role_details?.village,
          '#isDisabled': true,
        },
        gram_panchayat: {
          ...role_details?.gram_panchayat,
          '#isDisabled': true,
        },
        block: {
          ...role_details?.block,
          '#isDisabled': true,
        },
        health_block: {
          ...role_details?.health_block,
          '#isDisabled': true,
        },
        sub_division: {
          ...role_details?.sub_division,
          '#isDisabled': true,
        }
      }));
      setCoverageShiftTransferData((coverage_details: any) => ({
        ...coverage_details,
        hfr_id: {
          ...coverage_details?.hfr_id,
          '#isDisabled': true,
        },
        health_facility_type: {
          ...coverage_details?.health_facility_type,
          '#isDisabled': true,
        },
        health_facility_name: {
          ...coverage_details?.health_facility_name,
          '#isDisabled': true,
        },
        mobiliser_tagged: {
          ...coverage_details?.mobiliser_tagged,
          '#isDisabled': true,
        },
        wards_covered: {
          ...coverage_details?.wards_covered,
          '#isDisabled': true,
        },
        villages_covered: {
          ...coverage_details?.villages_covered,
          '#isDisabled': true,
        },
        gram_panchayat_covered: {
          ...coverage_details?.gram_panchayat_covered,
          '#isDisabled': true,
        }
      }));
    }

  }, [locationDetails, mobiliserTaggedDetails, isGrampanchayat]);

  const setFacilityCoverageData = async (facility_name_options: any, facility_hfr_options: any, facility_type_options: any, locationData?: any) => {
    setCoverageShiftTransferData((prevState: any) => ({
      ...prevState,
      health_facility_name: {
        ...prevState.health_facility_name,
        '#options': facility_name_options.length > 0 ? facility_name_options : [],
        '#isDisabled': locationData?.facility_details?.facility_name ? true : false,
      },
      hfr_id: {
        ...prevState.hfr_id,
        '#options': facility_hfr_options.length > 0 ? facility_hfr_options : [],
      },
      health_facility_type: {
        ...prevState.health_facility_type,
        '#options': facility_type_options.length > 0 ? facility_type_options : [],
        '#isDisabled': locationData?.facility_details?.facility_type_name ? true : false,
      },
    }));
  }

  useEffect(() => {
    if (formattedUserDetails.state_id && ulbInputText?.length >= 3) {
      fecthUlbBySearch(ulbInputText);
    }
    if (ulbInputText?.length === 0) {
      setShiftTransferData((hierarchy_details_urban: any) => ({
        ...hierarchy_details_urban,
        ulb: {
          ...hierarchy_details_urban.ulb,
          '#options': [],
        },
      }));
    }
  }, [ulbInputText]);

  const fetchVillageWardAndAshaShiftTransfer = async (facility_id: any) => {
    try {
      if (isGrampanchayat) {
        const gramPanchayat = await getData(`${endpoints.getGramPanchayatCoverageByFacilityId(facility_id)}`) as any;
        const gramPanchayatOptions = gramPanchayat.data.map((option1: any) => ({
          value: option1.gram_panchayat_name + "-" + option1.gram_panchayat_code,
          label: option1.gram_panchayat_name + "-" + option1.gram_panchayat_code,
          id: option1.gram_panchayat_id,
          code: option1.gram_panchayat_code,
        }));
        setCoverageShiftTransferData((coverage_details: any) => ({
          ...coverage_details,
          gram_panchayat_covered: {
            ...coverage_details?.gram_panchayat_covered,
            '#options': gramPanchayatOptions,
          },
        }));
      } else {
        const villageCoverageResponse = await getData(`${endpoints.getVillageByFacilityId(facility_id)}`) as any;
        let villageCovered_options = villageCoverageResponse?.data?.map((key: any) => ({
          value: key.village_name + "-" + key.village_code,
          label: key.village_name + "-" + key.village_code,
          id: key.village_id,
          code: key.village_code,
        })) || [];
        villageCovered_options = villageCovered_options.filter((item: any) => item.value && !item.value.includes('undefined'));
        setCoverageShiftTransferData((coverage_details: any) => ({
          ...coverage_details,
          villages_covered: {
            ...coverage_details?.villages_covered,
            '#options': villageCovered_options,
          },
        }));
      }

      const wardCoverageResponse = await getData(`${endpoints.getWardByFacilityId(facility_id)}`) as any;
      const wardCovered_options = wardCoverageResponse?.data?.map((key: any) => ({
        value: key.ward_name + "-" + key.ward_code,
        label: key.ward_name + "-" + key.ward_code,
        id: key.ward_id,
        code: key.ward_code,
      }));

      setCoverageShiftTransferData((coverage_details: any) => ({
        ...coverage_details,
        wards_covered: {
          ...coverage_details?.wards_covered,
          '#options': wardCovered_options,
        },
      }));

      let mobiliserTaggedOptions = [];

      if (formattedUserDetails?.usertype_name === SHIFT_TRANSFER_CONSTANTS.USER_TYPES.FACILITY_LEVEL) {
        const mobiliserTaggedDetail = await getData(`${endpoints.getASHAMobiliserTagged(facility_id)}`) as any;
        mobiliserTaggedOptions = mobiliserTaggedDetail.data.map((key: any) => ({
          value: key.f_name + " " + key.l_name,
          label: key.f_name + " " + key.l_name,
          id: key.user_id,
          code: key.user_code
        }));
      } else if (formattedUserDetails?.usertype_name === SHIFT_TRANSFER_CONSTANTS.USER_TYPES.FIELD_LEVEL) {
        const mobiliserTaggedDetail = await getData(`${endpoints.getHealthProviderTagged(facility_id)}`) as any;
        mobiliserTaggedOptions = mobiliserTaggedDetail.data.map((key: any) => ({
          value: key.f_name + " " + key.l_name,
          label: key.f_name + " " + key.l_name,
          id: key.user_id,
          code: key.user_code
        }));
      }

      setCoverageShiftTransferData((coverage_details: any) => ({
        ...coverage_details,
        mobiliser_tagged: {
          ...coverage_details?.mobiliser_tagged,
          '#options': mobiliserTaggedOptions,
        },
      }));

      if (mobiliserTaggedDetails && mobiliserTaggedDetails.length > 0) {
        setTimeout(() => {
          const taggedValues = mobiliserTaggedDetails.map((item: any) => item.value);
          const taggedIds = mobiliserTaggedDetails.map((item: any) => item.id);
          const taggedCodes = mobiliserTaggedDetails.map((item: any) => item.code);

          setCoverageShiftTransferForm((prevState: any) => ({
            ...prevState,
            mobiliser_tagged: {
              ...prevState.mobiliser_tagged,
              value: taggedValues,
              id: taggedIds,
              code: taggedCodes,
              isError: false,
              message: ''
            }
          }));
        }, 100);
      }

    } catch (error: any) {
      errorLogger(error?.response?.data || error?.response || error);
    }
  };

  const fetchAndReturnHFRIdTypeAndName = useCallback(async (id: any, key: string, districtId?: any) => {
    let params: any;
    let endpoint: any;
    if (key === "village") {
      params = {
        p_village_id: id ?? userDetails?.village_id,
        p_facilitytype_id: '',
        p_search_facility: ''
      }
      endpoint = endpoints.getFacilityByVillage;
    } else if (key === "ward") {
      params = {
        p_ward_id: id ?? locationDetails.ward?.ward_id,
        p_facilitytype_id: '',
        p_search_facility: ''
      }
      endpoint = endpoints.getFacilityByWard;
    } else if (key === "gram_panchayat") {
      params = {
        p_gram_panchayat_id: id ?? userDetails?.gram_panchayat_id,
        p_facilitytype_id: '',
        p_search_facility: ''
      }
      endpoint = endpoints.getFacilityByGramPanchayat;
    }

    const facility_details_response = await getDataParams<any[]>(`${endpoint}`, params);

    const filteredFacilities = facility_details_response.data.filter((key: any) => {
      return key.hfr_id && key.facility_name;
    });
    setFacilityDetails1(filteredFacilities);
    let facility_hfr_options = facility_details_response.data.map((key: any) => ({
      value: key.hfr_id,
      label: key.hfr_id,
      id: key.facility_type_id
    }));

    facility_hfr_options = facility_hfr_options.filter((key: any) => {
      return key.value;
    });

    let facility_name_options = facility_details_response.data.map((key: any) => ({
      value: key.facility_name,
      label: key.facility_name,
      id: key.facility_type_id
    }));

    facility_name_options = facility_name_options.filter((key: any) => {
      return key.value;
    });

    const facility_typeResponse = await getData(endpoints.getFacilityType) as any;
    const facility_type_options = facility_typeResponse.data.map((key: any) => ({
      value: key.facility_type,
      label: key.facility_type,
      id: key.facility_type_id
    }));

    return { facility_hfr_options, facility_name_options, facility_type_options };
  }, [formattedUserDetails?.state_id, formattedUserDetails?.district_id]);

  const fecthUlbBySearch = async (ulbInputText: string) => {
    const getUlbwithsearch = await getData(`${endpoints.fetchUlbByStateIdAndSearch(formattedUserDetails.state_id, ulbInputText)}`) as any;
    setShiftTransferULBs(getUlbwithsearch?.data);
    const getUlb_options = getUlbwithsearch?.data?.map((key: any) => ({
      value: `${key.ulb_name}-${key.ulb_code}`,
      label: `${key.ulb_name}-${key.ulb_code}`,
      id: key.ulb_id,
      code: key.ulb_code,
    }));
    setShiftTransferData((hierarchy_details_urban: any) => ({
      ...hierarchy_details_urban,
      ulb: {
        ...hierarchy_details_urban.ulb,
        '#options': getUlb_options,
        '#required': true,
        '#isDisabled': false,
      },
    }));
  };

  const fetchShiftTransferData = useCallback(async (districtId?: any) => {
    try {
      if (isGrampanchayat) {
        setShiftTransferData((role_details: any) => ({
          ...role_details,
          village: {
            ...role_details?.village,
            '#isVisible': false,
          },
          gram_panchayat: {
            ...role_details?.gram_panchayat,
            '#isVisible': true,
          },
        }));
        setCoverageShiftTransferData((coverage_details: { villages_covered: any; gram_panchayat_covered: any }) => ({
          ...coverage_details,
          villages_covered: {
            ...coverage_details?.villages_covered,
            '#isVisible': false,
          },
          gram_panchayat_covered: {
            ...coverage_details?.gram_panchayat_covered,
            '#isVisible': true,
          },
        }));
      } else {
        setShiftTransferData((role_details: any) => ({
          ...role_details,
          village: {
            ...role_details?.village,
            '#isVisible': true,
          },
          gram_panchayat: {
            ...role_details?.gram_panchayat,
            '#isVisible': false,
          },
        }));
        setCoverageShiftTransferData((coverage_details: { villages_covered: any; gram_panchayat_covered: any }) => ({
          ...coverage_details,
          villages_covered: {
            ...coverage_details?.villages_covered,
            '#isVisible': true,
          },
          gram_panchayat_covered: {
            ...coverage_details?.gram_panchayat_covered,
            '#isVisible': false,
          },
        }));
      }

      const districtResponse = await getData(`${endpoints.getDistrictsWitoutUserId(formattedUserDetails.state_id)}`) as any;
      setShiftTransferDistricts(districtResponse?.data)
      const district_options = districtResponse?.data?.map((key: any) => ({
        value: key.district_name + "-" + key.district_code,
        label: key.district_name + "-" + key.district_code,
        id: key.district_id,
        code: key.district_code,
      }));

      setShiftTransferData((create_facilitydetails: { district: any; state: any; rural_urban: any; }) => ({
        ...create_facilitydetails,
        district: {
          ...create_facilitydetails.district,
          '#options': district_options,
        },
        state: {
          ...create_facilitydetails.state,
          "#isDisabled": true,
        },
        rural_urban: {
          ...create_facilitydetails.rural_urban,
          '#options': RURAL_URBAN_OPTIONS,
        },
      }));
    } catch (error: any) {
      errorLogger(error?.response?.data || error?.response || error);
    }
  }, [isGrampanchayat, formattedUserDetails?.state_id, fetchAndReturnHFRIdTypeAndName]);

  const initializeComponent = useCallback(async () => {
    setShowLoader(true);
    if (isInitializing || !formattedUserDetails?.state_code || hasInitialized.current) return;
    setIsInitializing(true);
    hasInitialized.current = true;

    try {
      let isUsingGramPanchayat: boolean = false;
      isUsingGramPanchayat = await isUsingGramPanchayatUtils(formattedUserDetails?.state_code);
      setIsGrampanchayat(isUsingGramPanchayat);

      if (formattedUserDetails?.usertype_name === SHIFT_TRANSFER_CONSTANTS.USER_TYPES.FACILITY_LEVEL) {
        setCoverageShiftTransferData((prevState) => ({
          ...prevState,
          mobiliser_tagged: {
            ...prevState['mobiliser_tagged'],
            '#title': SHIFT_TRANSFER_CONSTANTS.FORM_LABELS.MOBILISER_TAGGED,
          },
        }));
      } else if (formattedUserDetails?.usertype_name === SHIFT_TRANSFER_CONSTANTS.USER_TYPES.FIELD_LEVEL) {
        setCoverageShiftTransferData((prevState) => ({
          ...prevState,
          mobiliser_tagged: {
            ...prevState['mobiliser_tagged'],
            '#title': SHIFT_TRANSFER_CONSTANTS.FORM_LABELS.HEALTH_PROVIDER_TAGGED,
          },
        }));
      }

      setShiftTransferData((prevState: any) => ({
        ...prevState,
        village: {
          ...prevState.village,
          '#isVisible': !isUsingGramPanchayat,
        },
        gram_panchayat: {
          ...prevState.gram_panchayat,
          '#isVisible': isUsingGramPanchayat,
        },
      }));

      setCoverageShiftTransferData((prevState: any) => ({
        ...prevState,
        villages_covered: {
          ...prevState.villages_covered,
          '#isVisible': !isUsingGramPanchayat,
        },
        gram_panchayat_covered: {
          ...prevState.gram_panchayat_covered,
          '#isVisible': isUsingGramPanchayat,
        },
      }));

      await fetchShiftTransferData();
      await prepopulateFormData();

      setShowLoader(false);
    } catch (error) {
      errorLogger(error);
    } finally {
      setIsInitializing(false);
    }
  }, [
    formattedUserDetails?.state_code,
    formattedUserDetails?.usertype_name,
    formattedUserDetails?.state_name
  ]);

  useEffect(() => {
    if (formattedUserDetails?.state_code && !isInitializing && !hasInitialized.current) {
      initializeComponent();
    }
  }, [formattedUserDetails?.state_code, formattedUserDetails?.usertype_name, isInitializing]);

  const handleInputChange = _.debounce((value: string, key: string) => {
    if (key === 'ulb') {
      setUlbInputText(value.trim());
    }
  }, 500);

  const handleCancel = () => {
    setShiftTransferForm(shiftTransferObj);
    setCoverageShiftTransferForm(coverageShiftTransferObj);
    onCancel();
  };

  const handleSaveShiftTransfer = async () => {
    let checkError: any = false;
    if (!shiftTransferForm.state.value) {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        state: {
          ...prevState.state,
          isError: true,
          message: SHIFT_TRANSFER_CONSTANTS.ERROR_MESSAGES.STATE_REQUIRED,
        },
      }));
      checkError = true;
    }
    if (!shiftTransferForm.district.value) {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        district: {
          ...prevState.district,
          isError: true,
          message: SHIFT_TRANSFER_CONSTANTS.ERROR_MESSAGES.DISTRICT_REQUIRED,
        },
      }));
      checkError = true;
    }
    if (!shiftTransferForm.effective_from_date.value) {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        effective_from_date: {
          ...prevState.effective_from_date,
          isError: true,
          message: SHIFT_TRANSFER_CONSTANTS.ERROR_MESSAGES.EFFECTIVE_DATE_REQUIRED,
        },
      }));
      checkError = true;
    }
    if (!shiftTransferForm.rural_urban.value) {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        rural_urban: {
          ...prevState.rural_urban,
          isError: true,
          message: SHIFT_TRANSFER_CONSTANTS.ERROR_MESSAGES.RURAL_URBAN_REQUIRED,
        },
      }));
      checkError = true;
    }

    if (shiftTransferForm.rural_urban.value === SHIFT_TRANSFER_CONSTANTS.GEO_TYPES.URBAN && !shiftTransferForm.ulb.value) {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        ulb: {
          ...prevState.ulb,
          isError: true,
          message: SHIFT_TRANSFER_CONSTANTS.ERROR_MESSAGES.ULB_REQUIRED,
        },
      }));
      checkError = true;
    }
    if (!coverageShiftTransferForm.hfr_id.value) {
      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        hfr_id: {
          ...prevState.hfr_id,
          isError: true,
          message: SHIFT_TRANSFER_CONSTANTS.ERROR_MESSAGES.HFR_ID_REQUIRED,
        }
      }));
      checkError = true;
    }
    if (!coverageShiftTransferForm.health_facility_type.value) {
      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        health_facility_type: {
          ...prevState.health_facility_type,
          isError: true,
          message: SHIFT_TRANSFER_CONSTANTS.ERROR_MESSAGES.FACILITY_TYPE_REQUIRED,
        }
      }));
      checkError = true;
    }
    if (!coverageShiftTransferForm.health_facility_name.value) {
      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        health_facility_name: {
          ...prevState.health_facility_name,
          isError: true,
          message: SHIFT_TRANSFER_CONSTANTS.ERROR_MESSAGES.FACILITY_NAME_REQUIRED,
        }
      }));
      checkError = true;
    }

    if (!checkError) {
      const foundFacility = facilityDetails1?.find((item: any) => item.hfr_id === coverageShiftTransferForm.hfr_id.value);
      const facilityId = foundFacility?.facilit_id || foundFacility?.facility_id;

      const urbanRuralId = shiftTransferForm.rural_urban.value === SHIFT_TRANSFER_CONSTANTS.GEO_TYPES.URBAN ?
        SHIFT_TRANSFER_CONSTANTS.GEO_TYPES.URBAN_ID :
        SHIFT_TRANSFER_CONSTANTS.GEO_TYPES.RURAL_ID;
      const ashaMobilizerTaggedIds: any[] = [];
      mobiliserTaggedDetails?.forEach((key: any) => {
        if (coverageShiftTransferForm.mobiliser_tagged.value.includes(key.value)) {
          ashaMobilizerTaggedIds.push(key.id);
        }
      });

      healthProvider.current = hpShiftFinalData(facilityId, formattedUserDetails, ashaMobilizerTaggedIds, isGrampanchayat, urbanRuralId, shiftTransferForm, coverageShiftTransferForm);

      if (approvalLayout.requestData.requestFor == approvalConstantData.requestFor.sent) {
        await handleApprovalSubmit();
      } else {
        setShowApprovalModal(true);
      }

    }
  };

  const handleApprovalSubmit = async () => {
    const approveResubmitPayload = hpApprovalPayload(healthProvider.current)
    let statusChangedId = ''
    let status = ''
    if (approvalLayout.requestData.requestFor == approvalConstantData.requestFor.sent) {
      const pendingData = approvalLayout.approvalStatus.length > 0 ? approvalLayout.approvalStatus.filter((ele: any) => ele.code == STATUS.PENDING) : [];
      statusChangedId = pendingData.length > 0 ? pendingData[0].id : ''
      status = 'PENDING'
    } else {
      const approveData = approvalLayout.approvalStatus.length > 0 ? approvalLayout.approvalStatus.filter((ele: any) => ele.code == STATUS.APPROVED) : [];
      statusChangedId = approveData.length > 0 ? approveData[0].id : ''
      status = 'APPROVED'
    }
    const reducerData = approvalLayout.requestData;
    const comments = approvalLayout.comments

    const res = await approvalStatusUpdate(reducerData, comments, approveResubmitPayload, statusChangedId, true, userDetails?.user_name, dayjs(shiftTransferForm.effective_from_date.value).format('YYYY-MM-DD'), status, userDetails?.mobile_number);
    if (res) {
      if (approvalLayout.requestData.requestFor == approvalConstantData.requestFor.sent) {
        toast.success(errorSuccessMessage.reSubmit, {
          position: 'top-center',
        });
      } else {
        toast.success(errorSuccessMessage.approveSuccess, {
          position: 'top-center',
        });
      }
      navigate(`/approvals-flow`)
    }
  }

  const fetchShiftFieldOptionsById = async (selected_id: any, type: string) => {
    try {
      if (type === "district") {
        const sub_districtsResponse = await axios.get(`${endpoints.getSubDistricts(selected_id)}`, Auth.loggedIn());
        const sub_districts_options = sub_districtsResponse?.data?.data.map((key: any) => ({
          value: key.sub_district_name + '-' + key.sub_district_code,
          label: key.sub_district_name + '-' + key.sub_district_code,
          id: key.sub_district_id,
        }))
        const blockResponse = await axios.get(`${endpoints.getBlock(selected_id)}`, Auth.loggedIn());
        setShiftTransferBlocks(blockResponse?.data?.data)
        const block_options = blockResponse?.data?.data.map((key: any) => ({
          value: key.block_name + '-' + key.block_code,
          label: key.block_name + '-' + key.block_code,
          id: key.block_id,
        }))
        const health_blockResponse = await axios.get(`${endpoints.getHealthBlock(selected_id)}`, Auth.loggedIn());
        let health_block_options = health_blockResponse?.data?.data.map((key: any) => ({
          value: key.heal_block_name + "-" + key.heal_block_code,
          label: key.heal_block_name + "-" + key.heal_block_code,
          id: key.health_block_id
        }))
        health_block_options = health_block_options.filter((item: any) => item.value);
        const sub_divisionResponse = await axios.get(`${endpoints.getSubDivision(selected_id)}`, Auth.loggedIn());
        const sub_division_options = sub_divisionResponse?.data?.data.map((key: any) => ({
          value: key.sub_division_name + '-' + key.sub_division_code,
          label: key.sub_division_name + '-' + key.sub_division_code,
          id: key.sub_division_id
        }))
        setShiftTransferData((create_facilitydetails: { sub_district: any; block: any; health_block: any; sub_division: any; }) => ({
          ...create_facilitydetails,
          sub_district: {
            ...create_facilitydetails.sub_district,
            '#options': sub_districts_options,
          },
          block: {
            ...create_facilitydetails.block,
            '#options': block_options,
          },
          health_block: {
            ...create_facilitydetails.health_block,
            '#options': health_block_options,
          },
          sub_division: {
            ...create_facilitydetails.sub_division,
            '#options': sub_division_options,
          },
        }))
      } else if (type === "block") {
        if (isGrampanchayat) {
          const gramPanchayat = await getDataWithCache<any>(`${endpoints.getGramPanchayatByBlockId(selected_id)}`);
          const gramPanchayatOptions = gramPanchayat?.data.map((option1: any) => ({
            value: `${option1.gram_panchayat_name}-${option1.gram_panchayat_code}`,
            label: `${option1.gram_panchayat_name}-${option1.gram_panchayat_code}`,
            id: option1.gram_panchayat_id,
            code: option1.gram_panchayat_code
          }));
          setShiftTransferData((role_details: any) => ({
            ...role_details,
            gram_panchayat: {
              ...role_details.gram_panchayat,
              '#options': gramPanchayatOptions,
              '#isDisabled': false,
            },
          }))
        }
        else {
          const villagesResponse = await axios.get(`${endpoints.getVillageByBlockId(selected_id)}`, Auth.loggedIn());
          const village_options = villagesResponse?.data?.data.map((key: any) => ({
            value: key.village_name + "-" + key.village_code,
            label: key.village_name + "-" + key.village_code,
            id: key.village_id
          }))
          setShiftTransferData((create_facilitydetails: { village: any; }) => ({
            ...create_facilitydetails,
            village: {
              ...create_facilitydetails.village,
              '#options': village_options
            }
          }))
        }
      } else if (type === "ulb") {
        const wardResponse = await axios.get(`${endpoints.getWard(selected_id)}`, Auth.loggedIn());
        const ward_options = wardResponse?.data?.data.map((key: any) => ({
          value: key.ward_name + "-" + key.ward_code,
          label: key.ward_name + "-" + key.ward_code,
          id: key.ward_id
        }))
        setShiftTransferData((create_facilitydetails: { ward: any; }) => ({
          ...create_facilitydetails,
          ward: {
            ...create_facilitydetails.ward,
            '#options': ward_options
          }
        }))
      } else if (type === "hfr_id") {
        const mobiliserTaggedDetail = await axios.get(`${endpoints.getASHAMobiliserTagged(selected_id)}`, Auth.loggedIn());

        const mobiliser_tagged_options = mobiliserTaggedDetail.data.data.map((key: any) => ({
          value: key.f_name + " " + key.l_name,
          label: key.f_name + " " + key.l_name,
          id: key.user_id
        }));
        setCoverageShiftTransferData((create_facilitydetails: { mobiliser_tagged: any; }) => ({
          ...create_facilitydetails,
          mobiliser_tagged: {
            ...create_facilitydetails.mobiliser_tagged,
            '#options': mobiliser_tagged_options
          }
        }))
      }

    } catch (error: any) {
      errorLogger(error?.response?.data || error?.response || error)
    }
  }

  const handleShiftTransferFormChange = async (value: any, key: string, id?: any, option?: any, code?: any) => {
    if (value && key === "district") {
      if (value === userDetails?.district_name) {
        setShiftTransferForm((prevState: any) => ({
          ...prevState,
          district: {
            ...prevState['district'],
            value: value,
            id: id,
          },
          sub_district: {
            ...prevState['sub_district'],
            value: '',
            isError: false,
            message: '',
            id: '',
          },
          block: {
            ...prevState['block'],
            value: '',
            isError: false,
            message: '',
            id: '',
          },
        }));
      }
      const shiftTransferDistrict = shiftTransferDistricts.find((item: any) => item.district_name + '-' + item.district_code === value);
      await fetchShiftFieldOptionsById(shiftTransferDistrict.district_id, "district");
    } else if (!value && key === "district") {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        sub_district: {
          ...prevState['sub_district'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
        block: {
          ...prevState['block'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
        health_block: {
          ...prevState['health_block'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
        sub_division: {
          ...prevState['sub_division'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
        village: {
          ...prevState['village'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
        gram_panchayat: {
          ...prevState['gram_panchayat'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
        rural_urban: {
          ...prevState['rural_urban'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
        ulb: {
          ...prevState.ulb,
          value: '',
          id: ''
        },
        ward: {
          ...prevState.ward,
          value: '',
          id: ''
        }
      }));
      setShiftTransferData((create_facilitydetails: { sub_district: any; block: any; health_block: any; sub_division: any; village: any; gram_panchayat: any }) => ({
        ...create_facilitydetails,
        sub_district: {
          ...create_facilitydetails.sub_district,
          '#options': [],
        },
        block: {
          ...create_facilitydetails.block,
          '#options': [],
        },
        health_block: {
          ...create_facilitydetails.health_block,
          '#options': [],
        },
        sub_division: {
          ...create_facilitydetails.sub_division,
          '#options': [],
        },
        village: {
          ...create_facilitydetails.village,
          '#options': [],
        },
        gram_panchayat: {
          ...create_facilitydetails.gram_panchayat,
          '#options': [],
        },
      }))
    } else if (value && key === "block") {
      const shiftTransferBlock = shiftTransferBlocks?.find((item: any) => item?.block_name + '-' + item?.block_code === value);
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        village: {
          ...prevState['village'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
        gram_panchayat: {
          ...prevState['gram_panchayat'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
      }));
      await fetchShiftFieldOptionsById(shiftTransferBlock?.block_id, "block");
    } else if (!value && key === "block") {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        village: {
          ...prevState['village'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
        gram_panchayat: {
          ...prevState['gram_panchayat'],
          value: '',
          isError: false,
          message: '',
          id: '',
        },
      }));

      setShiftTransferData((create_facilitydetails: { village: any; gram_panchayat: any }) => ({
        ...create_facilitydetails,
        village: {
          ...create_facilitydetails.village,
          '#options': [],
        },
        gram_panchayat: {
          ...create_facilitydetails.gram_panchayat,
          '#options': [],
        },
      }));
    } else if (value && key === "ulb") {
      const shiftTransferULB = shiftTransferULBs?.find((item: any) => item.ulb_name + "-" + item.ulb_code === value);
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        ward: {
          ...prevState['ward'],
          value: '',
          isError: false,
          message: '',
          id: '',
        }
      }));
      await fetchShiftFieldOptionsById(shiftTransferULB?.ulb_id, "ulb");
    } else if (!value && key === "ulb") {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        ward: {
          ...prevState['ward'],
          value: '',
          isError: false,
          message: '',
          id: '',
        }
      }));

      setShiftTransferData((create_facilitydetails: { ward: any; }) => ({
        ...create_facilitydetails,
        ward: {
          ...create_facilitydetails.ward,
          '#options': [],
        },
      }));

    } else if (value && key === "hfr_id") {
      const facilityDetail = facilityDetails1?.find((item: any) => item.hfr_id === value);
      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        health_facility_name: {
          ...prevState['health_facility_name'],
          value: facilityDetail?.facility_name,
          isError: false,
          message: '',
          id: facilityDetail?.facilit_id,
        },
        health_facility_type: {
          ...prevState['health_facility_type'],
          value: facilityDetail?.facility_type,
          isError: false,
          message: '',
          id: facilityDetail?.facility_type_id,
        },
      }));
      setCoverageShiftTransferData((create_facilitydetails: { health_facility_type: any, health_facility_name: any; }) => ({
        ...create_facilitydetails,
        health_facility_type: {
          ...create_facilitydetails.health_facility_type,
          '#isDisabled': true
        },
        health_facility_name: {
          ...create_facilitydetails.health_facility_name,
          '#isDisabled': true
        },
      }));
      await fetchShiftFieldOptionsById(facilityDetail?.facilit_id, "hfr_id");
      await fetchVillageWardAndAshaShiftTransfer(facilityDetail?.facilit_id);
    } else if (!value && key === "hfr_id") {
      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        mobiliser_tagged: {
          ...prevState.mobiliser_tagged,
          value: '',
          id: ''
        },
        health_facility_name: {
          ...prevState.health_facility_name,
          value: '',
          id: ''
        },
        health_facility_type: {
          ...prevState.health_facility_type,
          value: '',
          id: ''
        },
        wards_covered: {
          ...prevState.wards_covered,
          value: [],
          id: []
        },
        villages_covered: {
          ...prevState.villages_covered,
          value: [],
          id: []
        },
        gram_panchayat_covered: {
          ...prevState.gram_panchayat_covered,
          value: [],
          id: []
        },

      }));
      setCoverageShiftTransferData((create_facilitydetails: { mobiliser_tagged: any, health_facility_type: any, health_facility_name: any }) => ({
        ...create_facilitydetails,
        mobiliser_tagged: {
          ...create_facilitydetails.mobiliser_tagged,
          '#options': [],
        },
        health_facility_type: {
          ...create_facilitydetails.health_facility_type,
          '#isDisabled': false
        },
        health_facility_name: {
          ...create_facilitydetails.health_facility_name,
          '#isDisabled': false
        },
      }));
    } else if (value && key === 'health_facility_name') {
      const facilityDetail = facilityDetails1?.find((item: any) => item?.facility_name === value);

      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        hfr_id: {
          ...prevState[key],
          value: facilityDetail?.hfr_id,
          isError: false,
          message: '',
          id: facilityDetail?.facilit_id,
        },
        health_facility_type: {
          ...prevState[key],
          value: facilityDetail?.facility_type,
          isError: false,
          message: '',
          id: facilityDetail?.facility_type_id,
        },
      }));
      setCoverageShiftTransferData((create_facilitydetails: { health_facility_type: any, hfr_id: any; }) => ({
        ...create_facilitydetails,
        hfr_id: {
          ...create_facilitydetails.hfr_id,
          '#isDisabled': true
        },
        health_facility_type: {
          ...create_facilitydetails.health_facility_type,
          '#isDisabled': true
        },
      }));
      await fetchShiftFieldOptionsById(facilityDetail?.facilit_id, "hfr_id");
      await fetchVillageWardAndAshaShiftTransfer(facilityDetail?.facilit_id);

    } else if (!value && key === 'health_facility_name') {
      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        mobiliser_tagged: {
          ...prevState.mobiliser_tagged,
          value: '',
          id: ''
        },
        hfr_id: {
          ...prevState.hfr_id,
          value: '',
          id: ''
        },
        health_facility_type: {
          ...prevState.health_facility_type,
          value: '',
          id: ''
        },
        wards_covered: {
          ...prevState.wards_covered,
          value: [],
          id: []
        },
        villages_covered: {
          ...prevState.villages_covered,
          value: [],
          id: []
        },
        gram_panchayat_covered: {
          ...prevState.gram_panchayat_covered,
          value: [],
          id: []
        },

      }));
      setCoverageShiftTransferData((create_facilitydetails: { mobiliser_tagged: any, health_facility_type: any, hfr_id: any }) => ({
        ...create_facilitydetails,
        mobiliser_tagged: {
          ...create_facilitydetails.mobiliser_tagged,
          '#options': [],
        },
        hfr_id: {
          ...create_facilitydetails.hfr_id,
          '#isDisabled': false
        },
        health_facility_type: {
          ...create_facilitydetails.health_facility_type,
          '#isDisabled': false
        },
      }));
    } else if (value && key === 'health_facility_type') {
      const filteredFacilityDetails: any[] = facilityDetails1?.filter((item: any) => item.facility_type === value && item.hfr_id && item.facility_name) || [];
      const facility_hfr_options = filteredFacilityDetails?.map((key: any) => ({
        value: key.hfr_id,
        label: key.hfr_id,
        id: key.facility_type_id
      }))

      const facility_name_options = filteredFacilityDetails?.map((key: any) => ({
        value: key.facility_name,
        label: key.facility_name,
        id: key.facility_type_id
      }))

      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        mobiliser_tagged: {
          ...prevState.mobiliser_tagged,
          value: '',
          id: ''
        },
        hfr_id: {
          ...prevState.hfr_id,
          value: '',
          id: ''
        },
        health_facility_name: {
          ...prevState.health_facility_name,
          value: '',
          id: ''
        },
      }));
      setCoverageShiftTransferData((create_facilitydetails: { mobiliser_tagged: any, wards_covered: any; villages_covered: any; gram_panchayat_covered: any, hfr_id: any, health_facility_name: any; }) => ({
        ...create_facilitydetails,
        mobiliser_tagged: {
          ...create_facilitydetails.mobiliser_tagged,
          '#options': [],
        },
        wards_covered: {
          ...create_facilitydetails.wards_covered,
          '#options': [],
        },
        villages_covered: {
          ...create_facilitydetails.villages_covered,
          '#options': [],
        },
        gram_panchayat_covered: {
          ...create_facilitydetails.gram_panchayat_covered,
          '#options': [],
        },
        health_facility_name: {
          ...create_facilitydetails.health_facility_name,
          '#options': facility_name_options,
        },
        hfr_id: {
          ...create_facilitydetails.hfr_id,
          '#options': facility_hfr_options,
        },
      }));
    } else if (!value && key === 'health_facility_type') {
      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        mobiliser_tagged: {
          ...prevState.mobiliser_tagged,
          value: '',
          id: ''
        },
        hfr_id: {
          ...prevState.hfr_id,
          value: '',
          id: ''
        },
        health_facility_name: {
          ...prevState.health_facility_name,
          value: '',
          id: ''
        },
        wards_covered: {
          ...prevState.wards_covered,
          value: [],
          id: []
        },
        villages_covered: {
          ...prevState.villages_covered,
          value: [],
          id: []
        },
        gram_panchayat_covered: {
          ...prevState.gram_panchayat_covered,
          value: [],
          id: []
        },

      }));

      let facility_hfr_options = facilityDetails1?.map((key: any) => ({
        value: key.hfr_id,
        label: key.hfr_id,
        id: key.facility_type_id
      }))

      let facility_name_options = facilityDetails1?.map((key: any) => ({
        value: key.facility_name,
        label: key.facility_name,
        id: key.facility_type_id
      }))

      facility_hfr_options = facility_hfr_options?.filter((key) => key.value);
      facility_name_options = facility_name_options?.filter((key) => key.value)

      setCoverageShiftTransferData((create_facilitydetails: { mobiliser_tagged: any; }) => ({
        ...create_facilitydetails,
        mobiliser_tagged: {
          ...create_facilitydetails.mobiliser_tagged,
          '#options': [],
        },
      }));

      setCoverageShiftTransferData((create_facilitydetails: { mobiliser_tagged: any, wards_covered: any, villages_covered: any, gram_panchayat_covered: any, hfr_id: any, health_facility_name: any; }) => ({
        ...create_facilitydetails,
        mobiliser_tagged: {
          ...create_facilitydetails.mobiliser_tagged,
          '#options': [],
        },
        wards_covered: {
          ...create_facilitydetails.wards_covered,
          '#options': [],
        },
        villages_covered: {
          ...create_facilitydetails.villages_covered,
          '#options': [],
        },
        gram_panchayat_covered: {
          ...create_facilitydetails.gram_panchayat_covered,
          '#options': [],
        },
        health_facility_name: {
          ...create_facilitydetails.health_facility_name,
          '#options': facility_name_options,
        },
        hfr_id: {
          ...create_facilitydetails.hfr_id,
          '#options': facility_hfr_options,
        },
      }));

    } else if (value && key === "rural_urban") {
      handleFieldsForUrbanRural(value);
      value === "Urban" ?
        setShiftTransferForm((prevState: any) => ({
          ...prevState,
          village: {
            ...prevState.village,
            value: '',
            id: ''
          },
          gram_panchayat: {
            ...prevState.gram_panchayat,
            value: '',
            id: '',
          },
          block: {
            ...prevState.block,
            value: '',
            id: ''
          },
        }))
        : setShiftTransferForm((prevState: any) => ({
          ...prevState,
          ulb: {
            ...prevState.ulb,
            value: '',
            id: ''
          },
          ward: {
            ...prevState.ward,
            value: '',
            id: ''
          },
        }))
    } else if (!value && key === "rural_urban") {
      handleFieldsForUrbanRural(value);
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        village: {
          ...prevState.village,
          value: '',
          id: ''
        },
        gram_panchayat: {
          ...prevState.gram_panchayat,
          value: '',
          id: '',
        },
        block: {
          ...prevState.block,
          value: '',
          id: ''
        },
        ulb: {
          ...prevState.ulb,
          value: '',
          id: ''
        },
        ward: {
          ...prevState.ward,
          value: '',
          id: ''
        },
      }))
    } else if (value && key === "village") {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        village: {
          ...prevState.village,
          value: value,
          id: id,
          isError: false,
          message: '',
        },
      }))
      const { facility_hfr_options, facility_name_options, facility_type_options } = await fetchAndReturnHFRIdTypeAndName(id, key);
      await setFacilityCoverageData(facility_name_options, facility_hfr_options, facility_type_options)
      await setFacilityCoverageForm();

    } else if (!value && key === "village") {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        village: {
          ...prevState.village,
          value: '',
          id: '',
        },
      }))
      await setFacilityCoverageData([], [], []);
      await setFacilityCoverageForm();
    } else if (value && key === "ward") {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        ward: {
          ...prevState.ward,
          value: value,
          id: id,
          isError: false,
          message: '',
        },
      }))

      const { facility_hfr_options, facility_name_options, facility_type_options } = await fetchAndReturnHFRIdTypeAndName(id, key);
      await setFacilityCoverageData(facility_name_options, facility_hfr_options, facility_type_options)
      await setFacilityCoverageForm();
    } else if (!value && key === "ward") {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        ward: {
          ...prevState.ward,
          value: '',
          id: '',
        },
      }));
      await setFacilityCoverageData([], [], []);
      await setFacilityCoverageForm();
    } else if (value && key === "gram_panchayat") {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        gram_panchayat: {
          ...prevState.gram_panchayat,
          value: value,
          id: id,
          isError: false,
          message: '',
        },
      }))
      const { facility_hfr_options, facility_name_options, facility_type_options } = await fetchAndReturnHFRIdTypeAndName(id, key);
      await setFacilityCoverageData(facility_name_options, facility_hfr_options, facility_type_options)
      await setFacilityCoverageForm();

    } else if (!value && key === "gram_panchayat") {
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        gram_panchayat: {
          ...prevState.gram_panchayat,
          value: '',
          id: '',
        },
      }))
      await setFacilityCoverageData([], [], []);
      await setFacilityCoverageForm();
    }
    if (key === 'health_facility_type' || key === "hfr_id" || key === "health_facility_name" || key === "villages_covered" || key === 'wards_covered' || key === 'gram_panchayat_covered' || key === "mobiliser_tagged") {
      setCoverageShiftTransferForm((prevState: any) => ({
        ...prevState,
        [key]: {
          ...prevState[key],
          value: value,
          isError: false,
          message: '',
          id: id,
          code: code
        },
      }))
    }
    setCoverageShiftTransferForm((prevState: any) => ({
      ...prevState,
      health_facility_name: {
        ...prevState.health_facility_name,
        isError: false,
        message: '',
      },
      hfr_id: {
        ...prevState.hfr_id,
        isError: false,
        message: '',
      },
      health_facility_type: {
        ...prevState.health_facility_type,
        isError: false,
        message: '',
      },
    }));
    setShiftTransferForm((prevState: any) => ({
      ...prevState,
      [key]: {
        ...prevState[key],
        value: value,
        isError: false,
        message: '',
        id: id,
        code: code
      },
    }));
  }

  const setFacilityCoverageForm = async () => {
    setCoverageShiftTransferForm((prevState: any) => ({
      ...prevState,
      mobiliser_tagged: {
        ...prevState.mobiliser_tagged,
        value: '',
        id: ''
      },
      hfr_id: {
        ...prevState.hfr_id,
        value: '',
        id: ''
      },
      health_facility_name: {
        ...prevState.health_facility_name,
        value: '',
        id: ''
      },
      health_facility_type: {
        ...prevState.health_facility_type,
        value: '',
        id: ''
      },
      wards_covered: {
        ...prevState.wards_covered,
        value: [],
        id: []
      },
      villages_covered: {
        ...prevState.villages_covered,
        value: [],
        id: []
      },
      gram_panchayat_covered: {
        ...prevState.gram_panchayat_covered,
        value: [],
        id: []
      }
    }));
  }

  const handleFieldsForUrbanRural = (shiftTransferRuralUrban: any) => {
    if (shiftTransferRuralUrban === "Rural") {
      setShiftTransferData((role_details: any) => ({
        ...role_details,
        ulb: {
          ...role_details?.ulb,
          '#isDisabled': true,
          '#required': false,
        },
        ward: {
          ...role_details?.ward,
          '#isDisabled': true,
          '#required': false,
        },
        village: {
          ...role_details?.village,
          '#isDisabled': false,
          '#required': false,
        },
        gram_panchayat: {
          ...role_details?.gram_panchayat,
          '#isDisabled': false,
          '#required': false,
        },
        block: {
          ...role_details?.block,
          '#isDisabled': false,
          '#required': false,
        },

      }));
      setShiftTransferForm((prevState: any) => ({
        ...prevState,
        ulb: {
          ...prevState.ulb,
          isError: false,
          message: '',
        },
      }));
      setCoverageShiftTransferData((coverage_details: { wards_covered: any; villages_covered: any; gram_panchayat_covered: any }) => ({
        ...coverage_details,
        wards_covered: {
          ...coverage_details?.wards_covered,
          '#isDisabled': true,
        },
        villages_covered: {
          ...coverage_details?.villages_covered,
          '#isDisabled': false,
        },
        gram_panchayat_covered: {
          ...coverage_details?.gram_panchayat_covered,
          '#isDisabled': false,
        },
      }));
    }
    else if (shiftTransferRuralUrban === "Urban") {
      setShiftTransferData((shift_transfer: any) => ({
        ...shift_transfer,
        ulb: {
          ...shift_transfer?.ulb,
          '#isDisabled': false,
          '#required': true,
        },
        ward: {
          ...shift_transfer?.ward,
          '#isDisabled': false,
          '#required': false,
        },
        village: {
          ...shift_transfer?.village,
          '#isDisabled': true,
          '#required': false,
        },
        gram_panchayat: {
          ...shift_transfer?.gram_panchayat,
          '#isDisabled': true,
          '#required': false,
        },
        block: {
          ...shift_transfer?.block,
          '#isDisabled': true,
          '#required': false,
        },

      }))
      setCoverageShiftTransferData((coverage_details: { wards_covered: any; villages_covered: any; gram_panchayat_covered: any }) => ({
        ...coverage_details,
        wards_covered: {
          ...coverage_details?.wards_covered,
          '#isDisabled': false,
        },
        villages_covered: {
          ...coverage_details?.villages_covered,
          '#isDisabled': true,
        },
        gram_panchayat_covered: {
          ...coverage_details?.gram_panchayat_covered,
          '#isDisabled': true,
        },
      }));
    } else if (shiftTransferRuralUrban === '' || shiftTransferRuralUrban === null || shiftTransferRuralUrban === undefined) {
      setShiftTransferData((shift_transfer: any) => ({
        ...shift_transfer,
        ulb: {
          ...shift_transfer?.ulb,
          '#isDisabled': true,
          '#required': false,
        },
        ward: {
          ...shift_transfer?.ward,
          '#isDisabled': true,
          '#required': false,
        },
        village: {
          ...shift_transfer?.village,
          '#isDisabled': true,
          '#required': false,
        },
        gram_panchayat: {
          ...shift_transfer?.gram_panchayat,
          '#isDisabled': true,
          '#required': false,
        },
        block: {
          ...shift_transfer?.block,
          '#isDisabled': true,
          '#required': false,
        },
      }))
      setCoverageShiftTransferData((coverage_details: { wards_covered: any; villages_covered: any; gram_panchayat_covered: any }) => ({
        ...coverage_details,
        wards_covered: {
          ...coverage_details?.wards_covered,
          '#isDisabled': true,
        },
        villages_covered: {
          ...coverage_details?.villages_covered,
          '#isDisabled': true,
        },
        gram_panchayat_covered: {
          ...coverage_details?.gram_panchayat_covered,
          '#isDisabled': true,
        },
      }));
    }
  }

  const handleCoverageFormChange = async (value: any, key: string, id?: any, option?: any, code?: any) => {
    if (key === 'villages_covered' || key === 'wards_covered' || key === 'gram_panchayat_covered') {
      id = [];
      option.length > 0 && value.length > 0 && option.forEach((op: any) => {
        if (value.includes(op.value)) {
          id.push(op.id);
        }
      });
      code = [];
      option.length > 0 && value.length > 0 && option.forEach((op: any) => {
        if (value.includes(op.value)) {
          code.push(op.code);
        }
      });
    }

    await handleShiftTransferFormChange(value, key, id, option, code);
  };

  return (
    <>
      {!showLoader ? (
        <>
          <div className="shift-transfer-component">
            <div className="shift-transfer-main-content">
              <div className="shift-transfer-header">
                <BadgesComponent
                  className="shift-transfer-badge"
                  badgeName={SHIFT_TRANSFER_CONSTANTS.FORM_LABELS.SHIFT_TRANSFER_TITLE}
                  variant="primary-1"
                  badgeType="case5"
                />
                <div className="shift-transfer-top-actions">
                  <Button
                    onClick={handleCancel}
                    disabled={false}
                    id="shift-transfer-cancel"
                    buttonName={SHIFT_TRANSFER_CONSTANTS.BUTTON_LABELS.CANCEL}
                    colorType="save"
                  />
                  {(approvalLayout.requestData.requestFor == approvalConstantData.requestFor.sent) && <Button
                    onClick={handleSaveShiftTransfer}
                    disabled={false}
                    id="shift-transfer-save-approve"
                    buttonName={SHIFT_TRANSFER_CONSTANTS.BUTTON_LABELS.SAVE_SUBMIT}
                    colorType={'primary'}
                  />}
                  {approvalLayout.requestData.requestFor == approvalConstantData.requestFor.received && <Button
                    onClick={handleSaveShiftTransfer}
                    disabled={false}
                    id="shift-transfer-save-approve"
                    buttonName={SHIFT_TRANSFER_CONSTANTS.BUTTON_LABELS.SAVE_APPROVE}
                    colorType="primary"
                  />}
                </div>
              </div>

              <div className="hierarchy-details-section">
                <div className="hierarchy-details-header">
                  <BadgesComponent
                    className="shift-transfer-badge"
                    badgeName={SHIFT_TRANSFER_CONSTANTS.FORM_LABELS.HIERARCHY_DETAILS}
                    variant="primary-1"
                    badgeType="case5"
                  />
                </div>

                <div className="shift-transfer-form-container">
                  <div className="shift-transfer-form-wrapper">
                    <form className="shift-transfer-form">
                      <CommonFormFields
                        formData={shiftTransferData}
                        formState={shiftTransferForm}
                        handleFormChange={handleShiftTransferFormChange}
                        onInputChange={handleInputChange}
                      />
                    </form>
                  </div>
                </div>
              </div>

              <div className="coverage-details-section">
                <div className="coverage-details-header">
                  <BadgesComponent
                    className="shift-transfer-badge"
                    badgeName={SHIFT_TRANSFER_CONSTANTS.FORM_LABELS.COVERAGE_DETAILS}
                    variant="primary-1"
                    badgeType="case5"
                  />
                </div>

                <div className="coverage-form-container">
                  <div className="coverage-form-wrapper">
                    <form className="coverage-form">
                      <CommonFormFields
                        formData={coverageShiftTransferData}
                        formState={coverageShiftTransferForm}
                        handleFormChange={handleCoverageFormChange}
                      />
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showApprovalModal && <ApprovalModal showModal={showApprovalModal} handleModalClose={() => setShowApprovalModal(false)} handleSubmit={handleApprovalSubmit} headerText='Reason for Approval' isCommentsRequired={false} />}
        </>
      ) : (
        <div className="loader">Loading...</div>
      )}
    </>

  );
};

export default ShiftTransferComponent;